import React, { useState, useContext, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import { useIsPhysicalTabletDetection } from "@/hooks/useIsPhysicalTabletDetection"
import { LanguageProviderContext } from "@/providers/LanguageProvider"
import NumericKeyboard from "@/components/NumericKeyboard"
import { X } from "lucide-react"

export interface InputProps extends React.ComponentProps<"input"> {
  hideSpinners?: boolean
  hideZero?: boolean
  showcustomNumPadKeyboard?: boolean
  keyboardMaxDecimals?: number
  clearable?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, hideSpinners, hideZero, value, showcustomNumPadKeyboard = true, keyboardMaxDecimals, onChange, onClick, clearable = false, ...props }, ref) => {
    const isTablet = useIsPhysicalTabletDetection()
    const { translator } = useContext(LanguageProviderContext)
    const [customNumPadKeyboard, setcustomNumPadKeyboard] = useState(false)
    const [tempValue, setTempValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const combinedRef = ref || inputRef

    const displayValue = hideZero && type === "number" && value === 0 ? '' : value;

    const showClear =
      clearable &&
      !props.disabled &&
      !props.readOnly &&
      displayValue != null &&
      displayValue !== ''
    
    useEffect(() => {
      if (customNumPadKeyboard) {
        const v = typeof value === 'string' ? value : value?.toString() ?? ''
        const empty = v === ''
        setTempValue(empty ? '0' : keyboardMaxDecimals === 2 ? (parseFloat(v)).toFixed(2) : v)
      }
    }, [customNumPadKeyboard, value, keyboardMaxDecimals])

    const handleConfirmKeyboard = () => {
      if (onChange) {
        const syntheticEvent = {
          target: { value: tempValue },
          currentTarget: { value: tempValue }
        } as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
      setcustomNumPadKeyboard(false)
    }

    const handleCancelKeyboard = () => {
      setcustomNumPadKeyboard(false)
    }

    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
      if (onClick) {
        onClick(e)
      }
      
      if (isTablet && type === "number" && showcustomNumPadKeyboard && !props.disabled && !props.readOnly) {
        setcustomNumPadKeyboard(true)
      }
    }

    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onChange?.({
        target: { value: '' },
        currentTarget: { value: '' }
      } as React.ChangeEvent<HTMLInputElement>)
    }

    const inputProps = {
      ...props,
      readOnly: isTablet && type === "number" && showcustomNumPadKeyboard ? true : props.readOnly
    }

    const inputElement = (
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            showClear && type === "number" && "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]",
            showClear && "pr-9 sm:pr-10",
            className
          )}
          ref={combinedRef}
          value={displayValue}
          onChange={onChange}
          onClick={handleInputClick}
          {...inputProps}
        />
    )

    const numericKeyboardPortal = customNumPadKeyboard && createPortal(
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div className="absolute bottom-20 right-0 w-80 h-auto max-h-[60vh] p-0 flex flex-col border-t border-l rounded-tl-lg bg-background shadow-lg pointer-events-auto">
              <NumericKeyboard
                value={tempValue}
                onChange={setTempValue}
                onConfirm={handleConfirmKeyboard}
                translator={translator}
                onCancel={handleCancelKeyboard}
              />
            </div>
          </div>,
          document.body
        )

    if (!clearable) {
      return (
        <>
          {inputElement}
          {numericKeyboardPortal}
        </>
      )
    }

    return (
      <>
        <div className="relative w-full min-w-0">
          {inputElement}
          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-1 sm:right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Clear"
            >
              <X className="h-4 w-4 shrink-0" />
            </button>
          )}
        </div>
        {numericKeyboardPortal}
      </>
    )
  }
)
Input.displayName = "Input"

export { Input }
